import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { hasPermission, PERMISSION_IDS, PERMISSION_NAMES } from '../../utils/permissions';
import { log } from '../../utils/logger';

const NAVIGATION_LINKS = [
  { label: 'Cursos', href: '/courses' },
  /* Eventos oculto temporalmente — restaurar: { label: 'Eventos', href: '/events' }, */
  /* Cursos Boutic oculto en navbar — restaurar: { label: 'Cursos Boutic', href: '/cursos-boutic' }, */
  { label: 'Tienda', href: '/shop' },
  { label: 'Asesorías', href: '/shop/asesorias' },
  { label: 'Noticias', href: '/newsletters' },
];

const ACCOUNT_LINKS = [
  {
    label: 'Cambiar contraseña',
    href: '/change-password',
    icon: 'fas fa-key',
  },
  {
    label: 'Mis Cursos',
    href: '/my-courses',
    icon: 'fas fa-graduation-cap',
  },
  {
    label: 'Mis Eventos',
    href: '/my-events',
    icon: 'fas fa-calendar-alt',
  },
  {
    label: 'Mis Asesorías',
    href: '/my-asesorias',
    icon: 'fas fa-comments',
  },
  {
    label: 'Mis Compras',
    href: '/my-purchases',
    icon: 'fas fa-shopping-bag',
  },
];

const ADMIN_LINK = {
  label: 'Panel Administrativo',
  href: '/admin',
  icon: 'fas fa-cog',
};

const formatCartCount = (count) => {
  if (!Number.isFinite(count) || count <= 0) return 0;
  return count > 99 ? '99+' : count;
};

const Navbar = () => {
  const { isAuthenticated, user, permissions, permissionIds, logout } = useAuth();
  const { cartCount } = useCart();
  const navigate = useNavigate();

  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isScrolled, setScrolled] = useState(false);

  const userMenuRef = useRef(null);

  const displayName = useMemo(() => {
    if (!user?.name) {
      return 'Usuario';
    }
    return user.name.length > 18 ? user.name.split(' ')[0] : user.name;
  }, [user]);

  const hasAdminAccess = useMemo(() => {
    if (!user) return false;
    
    // Verificar permiso VerBotonPanel por ID (EXCLUSIVAMENTE por ID)
    // Este permiso permite ver el botón en la barra de navegación para acceder al panel administrativo
    const hasAccess =
      hasPermission(user, PERMISSION_IDS.VER_BOTON_PANEL) ||
      hasPermission(user, PERMISSION_IDS.TESTER);
    
    // Debug logging (solo en desarrollo)
    if (process.env.NODE_ENV === 'development' && user) {
      log('[Navbar] Admin Access Check:', {
        user,
        permissionIds: user?.permissionIds,
        permissions: user?.permissions,
        hasAccess,
        userRole: user?.role || user?.rol,
        willShow: hasAccess,
        requiredPermission: PERMISSION_IDS.VER_BOTON_PANEL,
      });
    }
    
    return hasAccess;
  }, [user]);

  const toggleMobileMenu = () => setMobileMenuOpen((prev) => !prev);
  const closeMobileMenu = () => setMobileMenuOpen(false);

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);
  const closeDropdown = () => setDropdownOpen(false);

  const handleLogout = () => {
    logout({ redirectTo: '/', shouldRedirect: true });
  };

  const handleNavLinkClick = (href) => {
    if (!href) return;
    closeMobileMenu();
    const isExternalPath = href.startsWith('http') || href.startsWith('pages/');
    if (isExternalPath) {
      window.location.assign(href);
      return;
    }
    navigate(href);
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target)
      ) {
        closeDropdown();
      }
    };

    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 16);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        closeMobileMenu();
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <header>
      <nav className={`dp-navbar ${isScrolled ? 'dp-navbar--scrolled' : ''}`}>
        <div className="dp-navbar-container">
          <a href="/" className="dp-navbar-logo">
            Alwaysaligners
          </a>

          <button
            type="button"
            className={`dp-mobile-menu-btn ${isMobileMenuOpen ? 'active' : ''}`}
            aria-expanded={isMobileMenuOpen}
            onClick={toggleMobileMenu}
            aria-label="Abrir menú de navegación"
          >
            <i className="fas fa-bars" aria-hidden="true" />
          </button>

          <ul className={`dp-navbar-nav ${isMobileMenuOpen ? 'active' : ''}`}>
            {NAVIGATION_LINKS.map((link) => (
              <li key={link.label} className="dp-navbar-item">
                <a
                  href={link.href}
                  className="dp-navbar-link"
                  onClick={(event) => {
                    event.preventDefault();
                    handleNavLinkClick(link.href);
                  }}
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>

          <div className="dp-navbar-actions">
            <a
              href="/cart"
              className="dp-cart-icon"
              title="Carrito"
              onClick={(event) => {
                event.preventDefault();
                handleNavLinkClick('/cart');
              }}
            >
              <i className="fas fa-shopping-cart" aria-hidden="true" />
              <span className="dp-cart-badge" aria-label={`${cartCount} elementos en el carrito`}>
                {formatCartCount(cartCount)}
              </span>
            </a>

            {!isAuthenticated ? (
              <a
                href="/login"
                className="btn btn-outline btn-ingresar"
                onClick={(event) => {
                  event.preventDefault();
                  handleNavLinkClick('/login');
                }}
              >
                <i className="fas fa-sign-in-alt" aria-hidden="true" />
                Ingresar
              </a>
            ) : (
              <div className="dp-user-menu" ref={userMenuRef}>
                <button
                  type="button"
                  className={`dp-user-btn ${isDropdownOpen ? 'active' : ''}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleDropdown();
                  }}
                  aria-expanded={isDropdownOpen}
                >
                  <i className="fas fa-user" aria-hidden="true" />
                  <span className="dp-user-name">{displayName}</span>
                  <i className="fas fa-chevron-down dp-user-chevron" aria-hidden="true" />
                </button>

                <div className={`dp-user-dropdown ${isDropdownOpen ? 'active' : ''}`}>
                  <div className="dp-user-dropdown-header">
                    <div className="dp-user-dropdown-name">{user?.name || 'Usuario'}</div>
                    {user?.email ? (
                      <div className="dp-user-dropdown-email">{user.email}</div>
                    ) : null}
                  </div>
                  <ul className="dp-user-dropdown-menu">
                    {ACCOUNT_LINKS.map((link) => (
                      <li key={link.label} className="dp-user-dropdown-item">
                        <a
                          href={link.href}
                          className="dp-user-dropdown-link"
                          onClick={(event) => {
                            event.preventDefault();
                            closeDropdown();
                            handleNavLinkClick(link.href);
                          }}
                        >
                          <i className={link.icon} aria-hidden="true" />
                          {link.label}
                        </a>
                      </li>
                    ))}
                    {hasAdminAccess ? (
                      <li className="dp-user-dropdown-item">
                        <a
                          href={ADMIN_LINK.href}
                          className="dp-user-dropdown-link admin-panel"
                          onClick={(event) => {
                            event.preventDefault();
                            closeDropdown();
                            handleNavLinkClick(ADMIN_LINK.href);
                          }}
                        >
                          <i className={ADMIN_LINK.icon} aria-hidden="true" />
                          {ADMIN_LINK.label}
                        </a>
                      </li>
                    ) : null}
                    <li className="dp-user-dropdown-item">
                      <div className="dp-user-dropdown-divider" />
                    </li>
                    <li className="dp-user-dropdown-item">
                      <button
                        type="button"
                        className="dp-user-dropdown-link logout"
                        onClick={() => {
                          closeDropdown();
                          handleLogout();
                        }}
                      >
                        <i className="fas fa-sign-out-alt" aria-hidden="true" />
                        Cerrar Sesión
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;

